import api from "@/lib/api";
import { fileToResizedBase64 } from "@/lib/image";
import { getAllPending, putPending, removePending, countPending } from "@/lib/outbox";

let syncing = false;

// Offline voice debrief: transcribe, match an existing lead or create one.
async function syncVoice(entry) {
  const fd = new FormData();
  fd.append("file", entry.audioBlob, entry.audioName || "debrief.webm");
  fd.append("contactInfo", "{}");
  let dr;
  try { ({ data: dr } = await api.post("/audio-debrief", fd)); } catch (e) {
    if ([413, 422].includes(e.response?.status)) return "skipped";
    throw e;
  }
  const tr = (dr.debrief?.transcript || "").trim();
  if (tr.length < 15) return "skipped";
  const { data: m } = await api.post("/leads/voice-match", { transcript: tr });
  if (m.lead) {
    await api.post(`/leads/${m.lead.id}/debriefs`, { debrief: dr.debrief, aiClassification: dr.aiClassification });
    return;
  }
  const c = m.contact || {};
  const contactInfo = c.firstName || c.lastName || c.company ? c : { ...c, company: "Prospect à identifier (débrief hors ligne)" };
  await api.post("/leads", { eventName: entry.eventName, captureMethod: "VOICE", contactInfo, debriefs: [dr.debrief], aiClassification: dr.aiClassification });
}

function pruneEmpty(obj) {
  const out = {};
  for (const k in obj) if (obj[k]) out[k] = obj[k];
  return out;
}

// Drains the offline outbox: create lead → deferred OCR → deferred voice debrief (which
// auto-regenerates the account synthesis server-side). Stops on network loss to retry later.
export async function syncOutbox(onProgress) {
  if (syncing || !navigator.onLine) return { synced: 0, remaining: await countPending() };
  syncing = true;
  let synced = 0;
  let skipped = 0;
  try {
    const pending = await getAllPending();
    for (const entry of pending) {
      try {
        if (entry.kind === "voice") {
          if ((await syncVoice(entry)) === "skipped") skipped += 1; else synced += 1;
          await removePending(entry.id); if (onProgress) onProgress(); continue;
        }
        let contactInfo = entry.contactInfo || {};
        if (entry.pendingOcr && entry.photoBlob) {
          try {
            const b64 = await fileToResizedBase64(entry.photoBlob);
            const { data } = await api.post("/scan-card", { imageBase64: b64 });
            contactInfo = { ...(data.contactInfo || {}), ...pruneEmpty(contactInfo) };
          } catch (_) { /* keep manually-entered contact if OCR fails */ }
        }
        const { data: lead } = await api.post("/leads", {
          eventName: entry.eventName,
          captureMethod: entry.captureMethod,
          contactInfo,
          aiClassification: entry.aiClassification || undefined,
        });
        if (entry.audioBlob) {
          const fd = new FormData();
          fd.append("file", entry.audioBlob, entry.audioName || "debrief.webm");
          fd.append("contactInfo", JSON.stringify(contactInfo));
          fd.append("history", "");
          const { data: d } = await api.post("/audio-debrief", fd, { headers: { "Content-Type": "multipart/form-data" } });
          await api.post(`/leads/${lead.id}/debriefs`, { debrief: d.debrief, aiClassification: d.aiClassification });
        }
        await removePending(entry.id);
        synced += 1;
        if (onProgress) onProgress();
      } catch (e) {
        if (!navigator.onLine) break; // offline again → retry on next reconnect
        await putPending({ ...entry, status: "error", lastError: String((e && e.message) || e) });
      }
    }
  } finally {
    syncing = false;
  }
  return { synced, skipped, remaining: await countPending() };
}
